import { ReactionBar } from '@/components/reactions/ReactionBar';
import { COMPOSE_EVENT, focusSibling, isInteractiveTarget, quoteOf, setHoveredReply, startReply, type ComposeDetail } from './reply';
import { memo, useRef, useState } from 'react';
import clsx from 'clsx';
import { CheckSquare, Download, FileText, MessageSquareReply, MoreHorizontal, Pencil, Pin, PinOff, RotateCw, RotateCcw, CircleCheck, SmilePlus, Trash2, Link2, ShieldCheck, Languages, Truck, Phone, Flag } from 'lucide-react';
import { openReport } from '@/features/moderation/report';
import { canCompleteFormPost, messageSheetActions, myChannelActions, shouldAutoRetry, splitAttachments, threadLine, type Message, type MessageSheetAction } from '@tradeoutlet/shared';
import { useMobileLayout } from '@/mobile/layout';
import { MessageSheet } from '@/mobile/MessageSheet';
import { LONG_PRESS_MS, swallowNextClick } from '@/mobile/longPress';
import { ImageGallery } from '@/components/media/ImageGallery';
import { api, errorMessage } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { qk } from '@/lib/queryClient';
import { useAuth } from '@/lib/auth';
import { fileSize, fullDateTime, timeOfDay } from '@/lib/format';
import { useMembers, useTaskLinks, useWorkspaceId } from '@/lib/hooks';
import { messageStore, type PendingMessage } from '@/lib/messageStore';
import { useUi } from '@/lib/store';
import { useNavigate } from 'react-router-dom';
import { useCanPlanJourneys } from '@/lib/navAccess';
import { openAttachment } from '@/lib/upload';
import { Avatar, Button, Menu, MenuItem, Textarea, ask } from '@/components/ui';
import { MessageBody } from './MessageBody';
import { useTranslation } from '@/lib/translate';
import { TranslateBar } from '@/features/ai/TranslateBar';
import { MeetingNotesCard } from '@/features/ai/MeetingNotes';
import { decodeMentions, encodeMentions } from './Composer';
import { cachedMention, decodeEntities, encodeEntities } from '@/lib/entityMentions';
import { isTaskFromMessageNote, systemNoteActor, systemNoteText, canForwardFrom, forwardOf, type MentionKind } from '@tradeoutlet/shared';
import { Forward as ForwardIcon, MessageSquare } from 'lucide-react';
import { ForwardCard, ForwardModal } from './Forward';
import { FormEditModal, FormPost } from '@/features/forms/FormPost';
import { PollCard } from '@/features/polls/PollCard';
import { InlineThread, useThreadPrefs } from './InlineThread';
import { AckBanner, AckDetailsModal, AckRequestModal } from '@/features/acks/Acks';
import { hereAsOpener, openerState } from '@/lib/opener';
import { MessageTaskModal } from '@/features/tasks/MessageTaskModal';
import { MessageTaskRow } from './MessageTaskRow';
import { useModuleOn } from '@/lib/modules';
import { PersonButton } from '@/features/people/PersonDrawer';
import { MessageTime } from '@/components/time/RelativeTime';
import { enterToSend } from '@/lib/enterToSend';
import { bubbleCorners, bubbleNames, type BubbleRun } from './bubbles';
import { callEvent, describeCall } from './callLines';
import { useStartCall } from '@/features/calls/useStartCall';
import { Video } from 'lucide-react';

export const QUICK_EMOJI = ['👍', '❤️', '😂', '🎉', '👀', '✅', '🙏', '🔥'];

function Attachments({ items }: { items: NonNullable<Message['attachments']> }) {
  const toast = useUi(s => s.toast);
  if (!items.length) return null;
  // Feedback #7: images inline (grid + lightbox); everything else keeps its file card.
  const { images, files } = splitAttachments(items);
  return (
    <div className="mt-1.5 space-y-2">
    {images.length > 0 && <ImageGallery images={images} />}
    {files.length > 0 && <div className="flex flex-wrap gap-2">
      {files.map(a => (
        <button key={a.id} onClick={() => openAttachment(a.id).catch(e => toast(errorMessage(e), 'error'))}
          className="group flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-accent-line dark:border-slate-700 dark:bg-slate-900">
          <FileText className="h-8 w-8 rounded bg-accent-soft p-1.5 text-accent-fg" />
          <span><span className="block max-w-56 truncate text-sm font-medium" data-attachment-name>{a.file_name}</span><span className="block text-xs text-slate-500">{fileSize(a.size_bytes)}</span></span>
          <Download className="h-4 w-4 text-slate-500 opacity-0 group-hover:opacity-100" />
        </button>
      ))}
    </div>}
    </div>
  );
}

export interface AckContext { canManage: boolean; label: string; memberCount: number }

export const MessageItem = memo(function MessageItem({ m, compact, inThread, flat, comment, canSetStatus, ack, bubble, callEnd }: { m: Message; compact: boolean; inThread?: boolean;
  /** t289u: the "call ended" note paired with this "call started" note (same call id), shown on one line. */
  callEnd?: Message;
  /** t278u: chat bubbles (conversations and their threads): yours on the right, others on the left, grouped into runs. */
  bubble?: BubbleRun;
  /** Entity threads (#45): one flat discussion, so "reply" quotes into the main composer instead of opening a thread. */
  flat?: boolean;
  /** t258u: a comment on an item (entity thread, also when not flat, e.g. the feed's comments preview). */
  comment?: boolean; canSetStatus?: boolean; ack?: AckContext }) {
  const { userId } = useAuth();
  const { byId } = useMembers();
  const openThread = useUi(s => s.openThread);
  const threadPrefs = useThreadPrefs();
  const inlineThreads = threadPrefs !== null;
  const toast = useUi(s => s.toast);
  const ws = useWorkspaceId();
  const editEnt = useRef(new Map<string, { kind: MentionKind; id: string }>());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [picker, setPicker] = useState(false);
  const [menu, setMenu] = useState(false);
  // Phone: long-press or the ⋯ button opens the action sheet instead of the hover bar and dropdown.
  const phone = useMobileLayout();
  const [sheet, setSheet] = useState(false);
  const navigate = useNavigate();
  const canPlanJourney = useCanPlanJourneys();
  const tasksOn = useModuleOn('tasks'); // t369u
  const [formEdit, setFormEdit] = useState(false);
  const [swipe, setSwipe] = useState(0);
  const touch = useRef<{ x: number; y: number; t: number; timer?: ReturnType<typeof setTimeout>; moved: boolean; fired?: boolean } | null>(null);
  const hadSelection = useRef(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [fwdOpen, setFwdOpen] = useState(false);
  // t136: the channel's forwarding setting (from the cached conversation; the server enforces it either way).
  const conv = useQuery({ queryKey: qk.conversation(m.conversation_id), queryFn: () => api.conversation(m.conversation_id), enabled: false }).data;
  const fwd = forwardOf(m);
  // t150: a task comment mirrored into the project channel.
  const taskComment = (m.metadata?.task_comment ?? null) as { task_id: string; key: string; title: string } | null;
  const openTaskPanel = useUi(s => s.openTask);
  const canForward = m.kind === 'user' && !m.deleted_at && (!conv || canForwardFrom(conv));
  const fromLabel = !conv ? 'a conversation' : conv.kind === 'channel' ? `#${conv.slug ?? conv.title}` : conv.kind === 'dm' ? 'a direct message' : conv.kind === 'group_dm' ? 'a group chat' : conv.kind === 'room' ? `the ad-hoc meeting room “${conv.title}”` : 'a discussion';
  const hasAck = Boolean((m.metadata as { ack?: unknown } | undefined)?.ack);
  const isForm = Boolean(m.form || (m.metadata as { form?: unknown } | undefined)?.form);
  // t518u: top-level form posts can be marked complete (a linked journey does it when its stop is done); the API checks who.
  // t543u: the same rule as the API, shared with the iOS app (canCompleteFormPost); the phone web uses this component too.
  const canComplete = canCompleteFormPost(m, { userId, canSetStatus, manage: conv?.my_permissions?.manage, canPlanJourneys: canPlanJourney });
  const isPoll = Boolean((m.metadata as { poll_id?: unknown } | undefined)?.poll_id) && !m.deleted_at;
  const sender = byId.get(m.sender_id);
  const mine = m.sender_id === userId;
  const isComment = Boolean(comment) || conv?.kind === 'entity';
  // t258u / t313u: nobody reacts to their own message or comment (chat included); the server refuses it too. Taking
  // away a reaction left from before still works (a tap on its pill).
  // t520u: the channel's permissions decide react, edit, delete and pin (the server enforces them either way).
  // t522u: also when reactions are turned off on the item these comments belong to (taking yours back still works).
  const acts = myChannelActions(conv ?? {});
  const noReact = mine || !acts.react || threadPrefs?.canReact === false;
  const canEditMine = mine && acts.edit_own;
  const canDelete = mine ? acts.delete_own : m.kind === 'user' && conv?.kind === 'channel' && Boolean(conv.my_permissions?.delete_any);
  const presence = useUi(s => s.presence[m.sender_id]);
  const taskLinks = useTaskLinks(m.conversation_id).get(m.id);
  const translatable = m.kind === 'user' && !m.deleted_at && Boolean(m.body.trim()) && !(m.metadata as { poll_id?: unknown; form?: unknown } | undefined)?.poll_id && !m.form;
  const channelAuto = useQuery({ queryKey: qk.conversation(m.conversation_id), queryFn: () => api.conversation(m.conversation_id), enabled: false }).data?.settings?.auto_translate;
  const tr = useTranslation('message', translatable ? m.id : null, { auto: m.sender_id !== userId, version: m.edited_at, channelDefault: channelAuto });

  if (m.kind === 'system' && (m.metadata as { event?: string; recording_id?: string } | undefined)?.event === 'meeting_notes' && (m.metadata as { recording_id?: string }).recording_id) return <MeetingNotesCard m={m} />;
  // t258u: a deleted comment leaves no trace unless replies hang off it.
  if (isComment && m.deleted_at && !m.reply_count) return null;
  // t641u: the old "turned this into a task" note is replaced by the task row under the message.
  if (isTaskFromMessageNote(m)) return null;
  if (m.kind === 'system') {
    // t289u: a call is one line (started + ended paired by call id); t289u also drops the rules either side of notes.
    const ev = callEvent(m);
    if (ev) return <CallLineRow start={ev === 'call.started' ? m : null} end={ev === 'call.ended' ? m : callEnd ?? null} />;
    const actorId = systemNoteActor(m), actorName = actorId ? byId.get(actorId)?.display_name : undefined;
    return (
      <div className="flex justify-center gap-2 px-5 py-1 text-center text-xs text-slate-500" data-testid="system-line">
        <span className="[&>[data-message-body]]:inline">{actorName && <><PersonButton userId={actorId!} name={actorName} className="rounded font-medium text-slate-600 hover:underline dark:text-slate-300" testId="system-line-actor">{actorName}</PersonButton> </>}<MessageBody body={systemNoteText(m.body, (m.metadata as { duration_s?: number } | undefined)?.duration_s)} /></span>
        <span className="text-slate-500">{timeOfDay(m.created_at)}</span>
      </div>
    );
  }

  const react = async (emoji: string) => {
    setPicker(false);
    const has = m.reactions?.find(r => r.emoji === emoji)?.user_ids.includes(userId ?? '');
    if (noReact && !has) return;
    try {
      const r = has ? await api.removeReaction(m.id, emoji) : await api.addReaction(m.id, emoji);
      messageStore.applyReactions(m.conversation_id, m.id, r.reactions);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };

  const startEdit = () => {
    const ent = decodeEntities(m.body, (k, id) => cachedMention(ws, k, id)?.label ?? null);
    editEnt.current = ent.picked;
    setDraft(decodeMentions(ent.text, id => byId.get(id)?.display_name ?? 'unknown').text);
    setEditing(true);
  };
  const saveEdit = async () => {
    const picked = new Map<string, string>();
    for (const id of m.mentions) { const n = byId.get(id)?.display_name; if (n) picked.set(n, id); }
    const { body, mentions } = encodeMentions(encodeEntities(draft.trim(), editEnt.current), picked);
    if (!body) return;
    if (body === m.body || body === m.body.trim()) { setEditing(false); return; } // t258u: nothing changed, nothing saved
    try {
      const updated = await api.editMessage(m.id, body, mentions);
      messageStore.applyUpdate(m.conversation_id, updated);
      setEditing(false);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const remove = async () => {
    if (!await ask(isComment ? { title: 'Delete this comment?', confirmLabel: 'Delete' } : 'Delete this message? This cannot be undone.')) return;
    try {
      await api.deleteMessage(m.id);
      messageStore.applyUpdate(m.conversation_id, { id: m.id, body: '', deleted_at: new Date().toISOString() });
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const toggleComplete = async () => {
    setMenu(false);
    try { messageStore.applyUpdate(m.conversation_id, await api.setMessageCompleted(m.id, !m.completed_at)); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const togglePin = async () => {
    setMenu(false);
    try {
      if (m.pinned) await api.unpinMessage(m.id); else await api.pinMessage(m.id);
      messageStore.applyUpdate(m.conversation_id, { id: m.id, pinned: !m.pinned });
    } catch (e) { toast(errorMessage(e), 'error'); }
  };

  const deleted = Boolean(m.deleted_at);
  // t546u: no Reply where this person can't reply (announcements-only or read-only channels, archived ones, comments
  // they can't add to). Comments know it from their thread's provider; chat from the channel's permissions.
  const mayReply = isComment && threadPrefs ? threadPrefs.canReply : !conv || (acts.reply && !conv.archived_at);
  const canReply = !deleted && !editing && m.kind === 'user' && mayReply;
  const reply = () => {
    setMenu(false); setPicker(false);
    if (flat) window.dispatchEvent(new CustomEvent<ComposeDetail>(COMPOSE_EVENT, { detail: { conversationId: m.conversation_id, parentId: null, quote: quoteOf(m.body) } }));
    else startReply(m, openThread);
  };
  const selectionText = () => window.getSelection()?.toString() ?? '';
  const rowHandlers = {
    onMouseEnter: () => setHoveredReply(canReply ? reply : null),
    onMouseLeave: () => setHoveredReply(null),
    // Double-click plain content to reply (not links/buttons/media, and not when you had text selected first).
    onMouseDown: (e: React.MouseEvent) => { if (e.detail === 1) hadSelection.current = selectionText().length > 0; },
    onDoubleClick: (e: React.MouseEvent) => {
      if (!canReply || isInteractiveTarget(e.target) || hadSelection.current) return;
      window.getSelection()?.removeAllRanges();
      reply();
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if ((e.key === 'r' || e.key === 'R') && !e.ctrlKey && !e.metaKey && !e.altKey && canReply) { e.preventDefault(); reply(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); focusSibling(e.currentTarget, -1); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); focusSibling(e.currentTarget, 1); }
      else if (e.key === 'Home') { e.preventDefault(); focusSibling(e.currentTarget, 'first'); }
      else if (e.key === 'End') { e.preventDefault(); focusSibling(e.currentTarget, 'last'); }
      else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) { e.preventDefault(); setMenu(true); }
    },
    // Right-click: our menu (Reply first), except on links, media or when text is selected (keep the browser's).
    onContextMenu: (e: React.MouseEvent) => {
      // Phone: a long-press is ours (the action sheet), never the browser's menu or text-selection callout.
      // Android sends contextmenu for a long-press (sometimes before our timer): treat it as the long-press.
      if (phone) {
        e.preventDefault();
        if (deleted || editing) return;
        const s0 = touch.current;
        if (s0) { clearTimeout(s0.timer); s0.fired = true; }
        window.getSelection()?.removeAllRanges();
        setSheet(true);
        return;
      }
      if (deleted || editing || isInteractiveTarget(e.target) || selectionText()) return;
      e.preventDefault(); setMenu(true);
    },
    // Touch: swipe right to reply; long-press for the menu.
    onTouchStart: (e: React.TouchEvent) => {
      const t0 = e.touches[0];
      touch.current = { x: t0.clientX, y: t0.clientY, t: Date.now(), moved: false, timer: setTimeout(() => {
        const s0 = touch.current;
        if (!s0 || s0.moved || deleted || editing) return;
        s0.fired = true;
        if (phone) { window.getSelection()?.removeAllRanges(); navigator.vibrate?.(10); setSheet(true); } else setMenu(true);
      }, LONG_PRESS_MS) };
    },
    onTouchCancel: () => { const s0 = touch.current; touch.current = null; if (s0) clearTimeout(s0.timer); setSwipe(0); },
    onTouchMove: (e: React.TouchEvent) => {
      const s0 = touch.current; if (!s0) return;
      const dx = e.touches[0].clientX - s0.x, dy = e.touches[0].clientY - s0.y;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) { s0.moved = true; clearTimeout(s0.timer); }
      if (canReply && dx > 0 && Math.abs(dy) < 30) setSwipe(Math.min(dx, 96));
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const s0 = touch.current; touch.current = null;
      if (s0) clearTimeout(s0.timer);
      // The long-press already acted: the release must not also tap what is under the finger (e.g. a row of the new sheet).
      if (s0?.fired) { if (e.cancelable) e.preventDefault(); swallowNextClick(); setSwipe(0); return; }
      if (swipe >= 64) reply();
      setSwipe(0);
    },
  };

  // t536u: anyone can report someone else's message (not their own, system notes or unsent ones).
  const canReport = !mine && m.kind === 'user' && !deleted;
  const quoteLabel = (inThread && m.parent_id) || flat ? 'Quote in reply' : undefined;
  const sheetActions = phone && sheet ? messageSheetActions({ mine, kind: m.kind, deleted, hasText: Boolean(m.body.trim()) && !isPoll, isPoll, taskComment: Boolean(taskComment) },
    { reply: canReply, forward: canForward, ack: (mine || Boolean(ack?.canManage)) && m.kind === 'user', translate: translatable && (tr.available || Boolean(tr.translated)), journey: canPlanJourney && !m.linked_journey, task: tasksOn,
      pin: acts.pin, edit: canEditMine, delete: canDelete, complete: canComplete, report: canReport }) : [];
  const plainText = () => decodeMentions(decodeEntities(m.body, (k, id) => cachedMention(ws, k, id)?.label ?? null).text, id => byId.get(id)?.display_name ?? 'unknown').text;
  const onSheet = (a: MessageSheetAction) => {
    if (a === 'reply') reply();
    else if (a === 'copy') { void navigator.clipboard?.writeText(plainText()).then(() => toast('Copied', 'success'), () => undefined); }
    else if (a === 'forward') setFwdOpen(true);
    else if (a === 'task') setTaskOpen(true);
    else if (a === 'ack') setAckOpen(true);
    else if (a === 'translate') { if (tr.translated) tr.showOriginal(); else tr.translate(); }
    else if (a === 'pin') void togglePin();
    else if (a === 'link') { void navigator.clipboard?.writeText(`${location.origin}/c/${m.conversation_id}${m.parent_id ? `?thread=${m.parent_id}` : `#m-${m.id}`}`); toast('Link copied', 'success'); }
    else if (a === 'complete') void toggleComplete();
    else if (a === 'journey') navigate(`/fleet/journeys/new?message=${m.id}`, { state: openerState(hereAsOpener()) });
    else if (a === 'edit') { if (isForm) { if (m.form) setFormEdit(true); } else startEdit(); }
    else if (a === 'delete') void remove();
    else if (a === 'report') openReport({ type: 'message', id: m.id });
  };

  const content = (
    <>
        {deleted ? (
          <div className="text-sm italic text-slate-500">{isComment ? 'Comment deleted' : 'This message was deleted.'}</div>
        ) : editing ? (
          <div className="mt-1 space-y-2">
            <Textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={3}
              onKeyDown={enterToSend({ value: draft, onSend: () => void saveEdit(), onCancel: () => setEditing(false) })} />
            <div className="flex gap-2"><Button size="sm" onClick={() => void saveEdit()}>Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button><span className="self-center text-xs text-slate-500">Enter to save · Esc to cancel</span></div>
          </div>
        ) : (
          <>
            {taskComment && <button type="button" onClick={() => openTaskPanel(taskComment.task_id)} data-testid="task-comment-marker" title="Comment on a task - open the task to reply there"
              className="mb-0.5 inline-flex max-w-full items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs text-slate-600 hover:border-black hover:bg-accent hover:text-on-accent dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"><MessageSquare className="h-3 w-3 shrink-0" />Comment on <span className="font-mono">{taskComment.key}</span><span className="truncate">{taskComment.title}</span></button>}
            {isForm ? <FormPost m={m} canSetStatus={canSetStatus} /> : isPoll ? <PollCard m={m} /> : fwd ? <>{fwd.has_comment && <MessageBody body={tr.translated?.text ?? m.body} />}<ForwardCard f={fwd} /></> : <MessageBody body={tr.translated?.text ?? m.body} />}
            {translatable && <TranslateBar t={tr} className="mt-0.5" />}
            {m.edited_at && <span className={clsx('text-[11px]', bubble ? 'text-bubble-meta' : 'text-slate-500')}> (edited)</span>}
            {m.kind === 'user' && Boolean((m.metadata as { call_id?: unknown } | undefined)?.call_id) && (
              <span className={clsx('ml-1 inline-flex items-center gap-0.5 align-middle text-[11px]', bubble ? 'text-bubble-meta' : 'text-slate-500')} data-testid="sent-during-call" title="Sent from the chat panel during a call">
                <Phone className="h-3 w-3" aria-hidden />sent during call
              </span>
            )}
            {m.attachments && !isForm && <Attachments items={m.attachments} />}
            {hasAck && <AckBanner m={m} />}
          </>
        )}
    </>
  );
  // t544u: the bar is raised while its menu or emoji picker is open, so the next messages' hover bars (also z-10) can't cover it.
  const actionsBar = (barPos: string) => (!deleted && !editing && !phone ? (
          <div data-testid="message-actions" className={clsx('absolute items-center rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800', barPos, picker || menu ? 'z-30 flex' : 'z-10 hidden group-hover:flex group-focus-visible:flex')}>
            {canReply && <button type="button" title={(inThread && m.parent_id) || flat ? 'Quote in reply (R)' : 'Reply in thread (R)'} aria-label="Reply" onClick={reply} data-testid="message-reply"
              className="flex items-center gap-1 rounded-l-lg border-r border-slate-200 px-2 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-700"><MessageSquareReply className="h-4 w-4" />Reply</button>}
            {!noReact && QUICK_EMOJI.slice(0, 3).map(e => <button key={e} onClick={() => void react(e)} className="px-1.5 py-1 hover:bg-slate-100 dark:hover:bg-slate-700">{e}</button>)}
            {!noReact && <div className="relative">
              <button title="Add reaction" onClick={() => setPicker(!picker)} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"><SmilePlus className="h-4 w-4" /></button>
              {picker && (
                <div className="absolute right-0 top-8 grid grid-cols-4 gap-1 rounded-lg bg-white p-2 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                  {QUICK_EMOJI.map(e => <button key={e} onClick={() => void react(e)} className="rounded p-1 text-lg hover:bg-slate-100 dark:hover:bg-slate-700">{e}</button>)}
                </div>
              )}
            </div>}
            {canEditMine && !isPoll && !taskComment && <button title="Edit" onClick={isForm ? () => m.form && setFormEdit(true) : startEdit} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"><Pencil className="h-4 w-4" /></button>}
            <Menu open={menu} setOpen={setMenu} trigger={<button title="More" aria-label="More" className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"><MoreHorizontal className="h-4 w-4" /></button>}>
              {canReply && <MenuItem icon={<MessageSquareReply className="h-4 w-4" />} onClick={reply}>{(inThread && m.parent_id) || flat ? 'Quote in reply' : 'Reply in thread'}</MenuItem>}
              {tasksOn && m.kind === 'user' && !m.deleted_at && <MenuItem icon={<CheckSquare className="h-4 w-4" />} onClick={() => { setMenu(false); setTaskOpen(true); }}>Create task or reminder</MenuItem>}
              {canForward && <MenuItem icon={<ForwardIcon className="h-4 w-4" />} onClick={() => { setMenu(false); setFwdOpen(true); }}>Forward…</MenuItem>}
              {canPlanJourney && !m.linked_journey && <MenuItem icon={<Truck className="h-4 w-4" />} onClick={() => { setMenu(false); navigate(`/fleet/journeys/new?message=${m.id}`, { state: openerState(hereAsOpener()) }); }}>Plan delivery journey</MenuItem>}
              {m.linked_journey && <MenuItem icon={<Truck className="h-4 w-4" />} onClick={() => { setMenu(false); navigate(`/fleet/journeys/${m.linked_journey!.id}`, { state: openerState(hereAsOpener()) }); }}>Open journey {m.linked_journey.ref}</MenuItem>}
              {canComplete && <MenuItem icon={m.completed_at ? <RotateCcw className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />} onClick={() => void toggleComplete()} data-testid="message-complete-toggle">{m.completed_at ? 'Reopen' : 'Mark complete'}</MenuItem>}
              {acts.pin && <MenuItem icon={m.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />} onClick={() => void togglePin()}>{m.pinned ? 'Unpin' : 'Pin to conversation'}</MenuItem>}
              {translatable && tr.available && !tr.translated && <MenuItem icon={<Languages className="h-4 w-4" />} onClick={() => { setMenu(false); tr.translate(); }}>Translate to {tr.targetName}</MenuItem>}
              {tr.translated && <MenuItem icon={<Languages className="h-4 w-4" />} onClick={() => { setMenu(false); tr.showOriginal(); }}>Show original</MenuItem>}
              <MenuItem icon={<Link2 className="h-4 w-4" />} onClick={() => { setMenu(false); void navigator.clipboard.writeText(`${location.origin}/c/${m.conversation_id}${m.parent_id ? `?thread=${m.parent_id}` : `#m-${m.id}`}`); toast('Link copied', 'success'); }}>Copy link</MenuItem>
              {(mine || ack?.canManage) && m.kind === 'user' && <MenuItem icon={<ShieldCheck className="h-4 w-4" />} onClick={() => { setMenu(false); setAckOpen(true); }}>{hasAck ? 'Acknowledgement settings…' : 'Require acknowledgement…'}</MenuItem>}
              {canDelete && <MenuItem danger icon={<Trash2 className="h-4 w-4" />} onClick={() => { setMenu(false); void remove(); }}>Delete message</MenuItem>}
              {canReport && <MenuItem icon={<Flag className="h-4 w-4" />} onClick={() => { setMenu(false); openReport({ type: 'message', id: m.id }); }}>Report</MenuItem>}
            </Menu>
          </div>
        ) : null);
  const threadBit = (
    <>
        {!inThread && m.reply_count > 0 && (inlineThreads
          ? <InlineThread root={m} ack={ack} bubbles={bubble ? bubble.names ?? bubbleNames(conv?.kind) : undefined} />
          : (
          phone ? (
          <button type="button" onClick={() => openThread(m.id)} data-testid="thread-line" className="mt-0.5 flex min-h-[44px] items-center gap-1.5 text-[13px] font-semibold text-accent-fg">
            <MessageSquareReply className="h-4 w-4" /><span>{threadLine(m.reply_count, null, timeOfDay)}{m.last_reply_at && <> · last <MessageTime iso={m.last_reply_at} inline testId="thread-last-reply" /></>}</span>
          </button>
          ) : <button onClick={() => openThread(m.id)} className="mt-1 flex items-center gap-2 rounded-md px-1 py-0.5 text-xs font-semibold text-accent-fg hover:bg-white dark:hover:bg-slate-800">
            <MessageSquareReply className="h-3.5 w-3.5" />{m.reply_count} {m.reply_count === 1 ? 'reply' : 'replies'}
            {m.last_reply_at && <span className="font-normal text-slate-500">Last reply <MessageTime iso={m.last_reply_at} inline testId="thread-last-reply" /></span>}
          </button>
        ))}
    </>
  );
  const extras = (
    <>
      {sheet && <StopBubbling><MessageSheet title={sender?.display_name ? `${sender.display_name}, ${timeOfDay(m.created_at)}` : 'Message'} emoji={noReact ? [] : QUICK_EMOJI.slice(0, 6)}
        reacted={e => Boolean(m.reactions?.find(r => r.emoji === e)?.user_ids.includes(userId ?? ''))} onReact={e => void react(e)}
        actions={sheetActions} onAction={onSheet} onClose={() => setSheet(false)}
        labels={{ ...(quoteLabel ? { reply: quoteLabel } : {}), pin: m.pinned ? 'Unpin' : 'Pin', translate: tr.translated ? 'Show original' : `Translate to ${tr.targetName}`, ack: hasAck ? 'Acknowledgement settings' : 'Require acknowledgement', complete: m.completed_at ? 'Reopen' : 'Mark complete' }} /></StopBubbling>}
      {formEdit && m.form && <FormEditModal m={m} onClose={() => setFormEdit(false)} />}
      {taskOpen && <MessageTaskModal message={m} onClose={() => setTaskOpen(false)} />}
      {fwdOpen && <ForwardModal message={m} fromLabel={fromLabel} onClose={() => setFwdOpen(false)} />}
      {ackOpen && (hasAck ? <AckDetailsModal m={m} onClose={() => setAckOpen(false)} />
        : <AckRequestModal draft={{ conversationId: m.conversation_id, channelLabel: ack?.label ?? 'this conversation', memberCount: ack?.memberCount ?? 0, message: m }} onClose={() => setAckOpen(false)} />)}
    </>
  );

  // t641u: the task made from this message, attached underneath like a reply (not a chip inside the bubble).
  const taskRows = taskLinks?.length ? (
    <div className={clsx('mt-1 flex flex-col gap-1', bubble && mine ? 'items-end' : 'items-start')} data-testid="message-task-rows">
      {taskLinks.map(l => <MessageTaskRow key={l.task_id} link={l} />)}
    </div>
  ) : null;

  if (bubble) {
    const run = bubble;
    const names = run.names ?? bubbleNames(conv?.kind);
    const avatarSlot = names && !mine;
    const nameShown = avatarSlot && run.first;
    const wide = isForm || isPoll || editing;
    const moreBtn = phone && !deleted && !editing && m.kind === 'user' ? (
      <button type="button" aria-label="Message actions" data-testid="message-more" onClick={() => setSheet(true)}
        className="flex h-11 w-7 shrink-0 items-center justify-center self-center text-slate-400 active:text-slate-700 dark:text-slate-500"><MoreHorizontal className="h-4 w-4" /></button>
    ) : null;
    const showMeta = run.last || m.pinned;
    return (
      <div id={`m-${m.id}`} data-message-row data-bubble={mine ? 'mine' : 'other'} tabIndex={-1} aria-label={`${mine ? 'You' : sender?.display_name ?? 'Message'}, ${timeOfDay(m.created_at)}`} {...rowHandlers}
        className={clsx('group relative flex flex-col px-3 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 sm:px-5', mine ? 'items-end' : 'items-start', run.first ? 'pt-2' : 'pt-0.5', phone && 'no-callout')}
        style={swipe ? { transform: `translateX(${swipe}px)`, transition: 'none' } : undefined}>
        {swipe > 0 && <span aria-hidden className="absolute -left-10 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700" style={{ opacity: Math.min(1, swipe / 64) }}><MessageSquareReply className="h-4 w-4" /></span>}
        {nameShown && (
          <div className="relative mb-0.5 flex items-center pl-10" data-testid="bubble-name">
            <PersonButton userId={m.sender_id} name={sender?.display_name} className="rounded text-xs font-semibold text-slate-500 hover:underline dark:text-slate-400">{sender?.display_name ?? 'Unknown'}</PersonButton>
            {actionsBar('left-full top-1/2 ml-2 -translate-y-1/2')}
          </div>
        )}
        <div className={clsx('flex w-full items-end gap-2', mine && 'justify-end')}>
          {avatarSlot && (
            <div className="w-8 shrink-0">
              {run.last && <PersonButton userId={m.sender_id} name={sender?.display_name} testId="bubble-avatar" className="block rounded-full"><Avatar size={32} name={sender?.display_name} id={m.sender_id} presence={presence} /></PersonButton>}
            </div>
          )}
          {mine && moreBtn}
          <div className={clsx('relative min-w-0', wide ? 'w-full max-w-xl' : 'max-w-[80%] sm:max-w-[70%]')} data-testid="message-block">
            <div data-testid="bubble" title={fullDateTime(m.created_at)}
              className={clsx('min-w-0 break-words text-[15px] sm:text-sm', bubbleCorners(mine, run),
                // Polls and forms are cards already: aligned to the sender's side without a second surround.
                editing || ((isForm || isPoll) && !deleted) ? undefined : deleted ? 'border border-dashed border-slate-300 px-3 py-1.5 dark:border-slate-700'
                  : mine ? 'bubble-mine bg-bubble-mine px-3 py-1.5 text-on-bubble-mine' : 'bg-bubble-other px-3 py-1.5 text-on-bubble-other')}>
              {content}
            </div>
            {taskRows}
            {!nameShown && actionsBar(mine ? '-top-4 right-0' : '-top-4 left-0')}
          </div>
          {!mine && moreBtn}
        </div>
        {(showMeta || (!deleted && (m.reactions?.length ?? 0) > 0) || (!inThread && m.reply_count > 0)) && (
          <div className={clsx('flex w-full flex-col', mine ? 'items-end' : 'items-start', avatarSlot && 'pl-10')}>
            {showMeta && (
              <div className="mt-0.5 flex items-center gap-1.5 px-1 text-[11px] text-slate-500" data-testid="bubble-meta">
                {m.pinned && <span className="flex items-center gap-0.5 text-orange-700"><Pin className="h-3 w-3" aria-hidden />Pinned</span>}
                {run.last && <MessageTime iso={m.created_at} />}
              </div>
            )}
            {!deleted && m.reactions && m.reactions.length > 0 && (
              <ReactionBar className={clsx('-mx-0.5 mt-0.5', mine && 'justify-end')} kind="message" id={m.id} pills={m.reactions} onToggle={e => void react(e)} />
            )}
            {!inThread && m.reply_count > 0 && <div className={clsx(inlineThreads && 'self-stretch')} data-testid="bubble-thread">{threadBit}</div>}
          </div>
        )}
        {extras}
      </div>
    );
  }

  return (
    <div id={`m-${m.id}`} data-message-row tabIndex={-1} aria-label={`${sender?.display_name ?? 'Message'}, ${timeOfDay(m.created_at)}`} {...rowHandlers}
      className={clsx('group relative flex gap-3 px-5 outline-none hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 dark:hover:bg-slate-900/60 dark:focus-visible:bg-slate-900/60', compact ? 'py-0.5' : 'pt-2 pb-0.5', m.pinned && 'bg-orange-50/60 dark:bg-orange-900/10', phone && 'no-callout')}
      style={swipe ? { transform: `translateX(${swipe}px)`, transition: 'none' } : undefined}>
      {swipe > 0 && <span aria-hidden className="absolute -left-10 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700" style={{ opacity: Math.min(1, swipe / 64) }}><MessageSquareReply className="h-4 w-4" /></span>}
      <div className="w-9 shrink-0">
        {compact
          ? <span className="invisible block pt-1 text-right text-[10px] text-slate-500 group-hover:visible" title={fullDateTime(m.created_at)}>{timeOfDay(m.created_at)}</span>
          : <PersonButton userId={m.sender_id} name={sender?.display_name} className="rounded-full"><Avatar size={36} name={sender?.display_name} id={m.sender_id} presence={presence} /></PersonButton>}
      </div>
      {phone && !deleted && !editing && m.kind === 'user' && (
        <button type="button" aria-label="Message actions" data-testid="message-more" onClick={() => setSheet(true)}
          className="absolute right-0 top-0 z-[1] flex h-11 w-11 items-center justify-center text-slate-400 active:text-slate-700 dark:text-slate-500"><MoreHorizontal className="h-4 w-4" /></button>
      )}
      <div className={clsx('min-w-0 flex-1', phone && 'pr-6')}>
        {/* The hover bar sits at the top-right of this block (the message itself), not the far edge of the pane. The block is at least 34rem wide so the bar (about 14rem) never covers the sender name or time, which open the profile. */}
        <div className="relative w-fit min-w-[min(100%,34rem)] max-w-full pr-2" data-testid="message-block">
        {!compact && (
          <div className="flex items-baseline gap-2">
            <PersonButton userId={m.sender_id} name={sender?.display_name} className="text-[14.5px] font-semibold text-slate-950 hover:underline dark:text-white">{sender?.display_name ?? 'Unknown'}</PersonButton>
            <MessageTime iso={m.created_at} className="text-xs text-slate-500" />
            {m.pinned && <span className="flex items-center gap-0.5 text-xs text-orange-700"><Pin className="h-3 w-3" />Pinned</span>}
          </div>
        )}
        {content}
        {taskRows}
        {actionsBar('-top-4 right-0')}
        </div>
        {!deleted && m.reactions && m.reactions.length > 0 && (
          <ReactionBar className="mt-1" kind="message" id={m.id} pills={m.reactions} onToggle={e => void react(e)} />
        )}
        {threadBit}
      </div>

      {extras}
    </div>
  );
});

export function PendingItem({ p, conversationId, bubble }: { p: PendingMessage; conversationId: string; bubble?: boolean }) {
  const state = p.status === 'sending' ? <span className="text-xs text-slate-500">Sending…</span> : (
    <span className={clsx('flex items-center gap-2 text-xs text-rose-600', bubble && 'flex-wrap justify-end')}>
      <span data-testid="pending-state">{shouldAutoRetry(p) ? 'Not sent · resends when you\'re back online' : `Not sent${p.error ? `: ${p.error}` : ''}`}</span>
      <button className="flex tap44 items-center gap-1 font-semibold hover:underline" onClick={() => void messageStore.retry(conversationId, p.client_message_id)}><RotateCw className="h-3 w-3" />Retry</button>
      <button className="tap44 hover:underline" onClick={() => messageStore.discard(conversationId, p.client_message_id)}>Discard</button>
    </span>
  );
  // t278u: in the bubble layout the local outbox shows as your own bubble, faded until the server has it.
  if (bubble) {
    return (
      <div className="flex flex-col items-end px-3 pt-0.5 sm:px-5" data-testid="pending-bubble">
        <div className="bubble-mine min-w-0 max-w-[80%] break-words rounded-2xl bg-bubble-mine px-3 py-1.5 text-on-bubble-mine opacity-70 sm:max-w-[70%]"><MessageBody body={p.body} /></div>
        <div className="mt-0.5 px-1">{state}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-3 px-5 py-0.5 opacity-70">
      <div className="w-9 shrink-0" />
      <div className="min-w-0 flex-1">
        <MessageBody body={p.body} />
        {state}
      </div>
    </div>
  );
}

/** t289u: one centred, muted line per call, naming who started it; the name opens the person sheet. */
function CallLineRow({ start, end }: { start: Message | null; end: Message | null }) {
  const { userId } = useAuth();
  const { byId } = useMembers();
  const startCall = useStartCall();
  const inThisCall = useUi(s => s.activeCall?.call.id);
  const c = describeCall(start, end, userId);
  if (!c) return null;
  const conversationId = (start ?? end)!.conversation_id;
  const name = byId.get(c.starterId)?.display_name ?? 'Someone';
  const Icon = c.media === 'video' ? Video : Phone;
  return (
    <div className={clsx('flex flex-wrap items-center justify-center gap-x-1.5 px-5 py-1 text-center text-xs', c.alert ? 'text-rose-700 dark:text-rose-300' : 'text-slate-500')}
      data-testid="call-line" data-state={c.state} data-call={c.callId}>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{c.prefix}{c.person && <PersonButton userId={c.starterId} name={name} className="font-medium hover:underline">{name}</PersonButton>}{c.suffix}</span>
      <span aria-hidden>·</span><time dateTime={c.at} title={fullDateTime(c.at)}>{timeOfDay(c.at)}</time>
      {c.details.map(d => <span key={d} className="contents"><span aria-hidden>·</span><span>{d}</span></span>)}
      {c.state === 'live' && inThisCall !== c.callId && (
        <button type="button" onClick={() => void startCall({ conversation_id: conversationId, media: c.media })} data-testid="call-line-join"
          className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 font-semibold text-slate-800 hover:border-black dark:border-slate-600 dark:text-slate-100">Join</button>
      )}
    </div>
  );
}

/** The phone sheet renders inside the message row: keep its taps and touches from reaching the row (long-press, swipe, double-tap). */
function StopBubbling({ children }: { children: React.ReactNode }) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return <div onClick={stop} onDoubleClick={stop} onMouseDown={stop} onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} onTouchCancel={stop} onContextMenu={stop} onKeyDown={stop}>{children}</div>;
}
